import { useState, useEffect, useRef } from 'react';
import { Popcorn, ChevronDown, Film, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { getWatchlist } from '../../lib/supabase';

interface WatchlistMovie {
  id: string;
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
  created_at: string;
}

export function WatchlistDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [movies, setMovies] = useState<WatchlistMovie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadWatchlist();
    }
  }, [isOpen]);

  const loadWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);
      const watchlist = await getWatchlist();
      setMovies(watchlist);
    } catch (err) {
      console.error('Error loading watchlist:', err);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'btn-quiet px-3 py-2 text-sm',
          isOpen && 'text-primary'
        )}
      >
        <Popcorn className="h-4 w-4" />
        Watchlist
        <ChevronDown className={cn(
          'h-4 w-4 transition-transform',
          isOpen && 'rotate-180 transform'
        )} />
      </button>

      {isOpen && (
        <div className="animate-fade-in absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-white/10 bg-popover shadow-deep">
          <div className="border-b border-white/[0.06] p-4">
            <h3 className="font-semibold text-foreground">My Watchlist</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-5 text-center">
                <div className="mx-auto h-1 w-28 overflow-hidden rounded-full bg-secondary">
                  <div className="animate-progress h-full rounded-full bg-primary" />
                </div>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-sm text-red-400">
                {error}
              </div>
            ) : movies.length > 0 ? (
              <>
                <div className="divide-y divide-white/[0.05]">
                  {movies.slice(0, 5).map((movie) => (
                    <div key={movie.id} className="p-4 transition-colors hover:bg-white/[0.04]">
                      <div className="flex gap-3">
                        <div className="poster-frame h-16 w-12 flex-shrink-0 !rounded-md">
                          {movie.poster_url ? (
                            <img
                              src={movie.poster_url}
                              alt={movie.title}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Film className="h-6 w-6 text-muted-foreground/50" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate font-medium text-foreground">
                            {movie.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {movie.year}
                          </p>
                          <p className="mt-1 line-clamp-2 text-sm leading-snug text-muted-foreground/80">
                            {movie.overview}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/[0.06] bg-white/[0.02] p-3.5">
                  <Link
                    to="/watchlist"
                    className="flex items-center justify-center gap-2 text-sm font-medium text-primary transition-colors hover:text-gold-soft"
                    onClick={() => setIsOpen(false)}
                  >
                    View All {movies.length > 0 && `(${movies.length})`}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </>
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <Film className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="mb-4 text-sm">Your watchlist is empty</p>
                <Link
                  to="/watchlist"
                  className="text-sm font-medium text-primary transition-colors hover:text-gold-soft"
                  onClick={() => setIsOpen(false)}
                >
                  View Watchlist
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}